import { useState } from 'react';

interface SetupScreenProps {
  onSubmit: (password: string) => Promise<void>;
  onSkip: () => void;
}

export function SetupScreen({ onSubmit, onSkip }: SetupScreenProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 4) {
      setError('Password must be at least 4 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await onSubmit(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed.');
      setBusy(false);
    }
  };

  return (
    <div className="lock-screen">
      <form className="lock-card" onSubmit={handleSubmit}>
        <div className="lock-icon" aria-hidden="true">
          <ShieldGlyph />
        </div>
        <h1 className="lock-title">Create password</h1>
        <p className="lock-description">
          This password encrypts your study data. There is no recovery mechanism —
          if you forget it, your data cannot be recovered.
        </p>

        <div className="lock-field">
          <label className="field-label" htmlFor="setup-password">
            Password
          </label>
          <input
            id="setup-password"
            type="password"
            className="text-input"
            value={password}
            autoFocus
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="lock-field">
          <label className="field-label" htmlFor="setup-confirm">
            Confirm password
          </label>
          <input
            id="setup-confirm"
            type="password"
            className="text-input"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        {error ? <p className="lock-error" role="alert">{error}</p> : null}

        <div className="lock-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !password || !confirm}
          >
            {busy ? 'Encrypting…' : 'Create password'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onSkip}
            disabled={busy}
          >
            Skip
          </button>
        </div>
      </form>
    </div>
  );
}

function ShieldGlyph() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
